/**
 * 加注尺度面板 —— 现在住在**底部抽屉**里（`.dock__sheet`），只有点了「加注」才浮出。
 *
 * 为什么从常驻改成抽屉（用户抱怨的 P3）：档位 + 滑块加起来约 90px，而一手牌里真正
 * 需要它的时刻不到两成。常驻等于让八成的时间为两成的功能付屏幕。抽屉是 absolute
 * 锚在 dock 上边缘的，展开时**不把牌桌顶上去**，dock 盒高也不变。
 *
 * 版式（自上而下，一屏读完）：
 *   加注到 [大号金额]        手动输入 · 收起×
 *   ├────────●──────────┤   最小加注 ↔ 全下
 *   [⅓池][½池][⅔池][满池][全下]
 *   [ 确认：加注到 X ]        ← 由 ActionDock 通过 footer 传进来
 *
 * 三条硬约束决定了这个文件的写法：
 *  - 红线 #48：拖动中**不触发任何 React 写入**。滑块的 input 回调只改 ref + 直接写
 *    DOM 的 textContent / transform，`change`（pointerup）才 setState。
 *  - 红线 #18：所有金额都是 raise-to，文案一律写「加注到」。
 *  - 红线 #47 同源：拖动是每帧路径，轨道宽度来自 ResizeObserver 缓存，不现场量。
 *
 * 手动输入用自绘键盘而不是 `<input type="number">`：iOS 弹出系统键盘会顶起
 * visual viewport，把 fixed 的三段式栅格整个推乱，回收时还会留下一段空白。
 *
 * 「全下」不在这里直接出手：点它只是把金额拉到 maxRaiseTo，真正提交要再点一次
 * footer 的确认键，而那一键此刻是 ALL-IN 态、需要长按（二次确认，防误触）。
 */

import { useCallback, useEffect, useMemo, useRef } from 'react'
import type { ReactNode, RefObject } from 'react'
import type { Chips, LegalActions } from '@poker/shared'

import { fireHaptic, type HapticPattern, type PulseTarget } from '../fx/haptics.js'
import { IconClose } from '../icons/index.js'
import {
  MAGNET_PX,
  SLIDER_STEPS,
  applyMagnet,
  buildPresetPills,
  createSliderMapping,
  formatChips,
  parseManualAmount,
  type PresetPill,
  type SliderMapping,
} from './useBetSizing.js'

export interface RaiseControlProps {
  readonly legal: LegalActions
  readonly bigBlind: Chips
  /** 已提交的选中值（raise-to）。拖动过程中它不变，松手才变。 */
  readonly value: Chips
  readonly onChange: (to: Chips) => void
  /**
   * 拖动中同步写入的镜像节点——确认键里的那个金额。
   * 用 ref 而不是回调，是为了让「每帧更新」这件事在类型上就不可能触发 setState。
   */
  readonly mirrorRef?: RefObject<HTMLElement | null>
  readonly haptics?: boolean
  /** 收起抽屉。抽屉右上角那个 × 与遮罩点击是同一个动作。 */
  readonly onClose?: () => void
  /** 确认键。长按确认的状态机在 ActionDock 里，这里只负责给它一个落脚点。 */
  readonly footer?: ReactNode
}

/**
 * 同 ActionDock：走 `fx/haptics` 的能力检测 + 降级，**不直接调 `navigator.vibrate`**
 * （DEVICES.md R4）。滑块磁吸的 'tick' 在 iOS 上会降级成一声 8ms 的短音，
 * 不传 target —— 拖动中给把手加视觉脉冲会和滑块本身的动效打架。
 */
function buzz(enabled: boolean, pattern: HapticPattern, target?: PulseTarget | null): void {
  fireHaptic(pattern, enabled, target ?? null)
}

/** 退格键图标。UI 规则：图标一律 SVG，不用字符画也不用 emoji。 */
function IconBackspace(): ReactNode {
  return (
    <svg
      viewBox="0 0 24 24"
      width="20"
      height="20"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.6"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d="M9 5h10a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H9l-6-7 6-7Z" />
      <path d="M18 9l-6 6M12 9l6 6" />
    </svg>
  )
}

export function RaiseControl({
  legal,
  bigBlind,
  value,
  onChange,
  mirrorRef,
  haptics = true,
  onClose,
  footer,
}: RaiseControlProps): ReactNode {
  const mapping = useMemo(() => createSliderMapping(legal, bigBlind), [legal, bigBlind])
  const pills = useMemo(() => buildPresetPills(legal), [legal])

  const inputRef = useRef<HTMLInputElement>(null)
  const trackRef = useRef<HTMLDivElement>(null)
  const fillRef = useRef<HTMLDivElement>(null)
  const amountRef = useRef<HTMLDivElement>(null)
  const padRef = useRef<HTMLDivElement>(null)
  const padValueRef = useRef<HTMLDivElement>(null)

  // 拖动路径上要读的东西全部放 ref：监听器只挂一次，不随渲染重建
  const widthRef = useRef(0)
  const liveRef = useRef(value)
  const lastMagnetRef = useRef<string | null>(null)
  const mappingRef = useRef<SliderMapping>(mapping)
  const pillsRef = useRef<readonly PresetPill[]>(pills)
  const hapticsRef = useRef(haptics)
  const onChangeRef = useRef(onChange)
  const padDraftRef = useRef('')
  mappingRef.current = mapping
  pillsRef.current = pills
  hapticsRef.current = haptics
  onChangeRef.current = onChange

  /** 把金额画到 DOM 上。这是拖动期间唯一允许发生的副作用。 */
  const paint = useCallback(
    (to: Chips, t: number): void => {
      const text = formatChips(to)
      if (amountRef.current !== null) amountRef.current.textContent = text
      if (mirrorRef?.current != null) mirrorRef.current.textContent = text
      // 自绘填充条用 scaleX 而不是 width：只走合成层，不触发布局（红线 #46）
      if (fillRef.current !== null) fillRef.current.style.transform = `scaleX(${t})`
    },
    [mirrorRef],
  )

  // 轨道宽度缓存：磁吸半径是 8 **像素**，要换算成 t 就得知道轨道多宽
  useEffect(() => {
    const el = trackRef.current
    if (el === null) return undefined
    const ro = new ResizeObserver((entries) => {
      const w = entries[0]?.contentRect.width
      if (typeof w === 'number') widthRef.current = w
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])

  // 原生监听器而不是 React 的 onInput：React 合成事件会走一遍调度器，
  // 而这里每帧都要跑，且刻意不能引起任何重渲染
  useEffect(() => {
    const el = inputRef.current
    if (el === null) return undefined

    const onInput = (): void => {
      const m = mappingRef.current
      if (!m.enabled) return
      const t = Number(el.value) / SLIDER_STEPS
      const magnetT = MAGNET_PX / Math.max(1, widthRef.current)
      const hit = applyMagnet(t, pillsRef.current, m, magnetT)
      const shownT = m.toT(hit.to)
      if (hit.kind !== null) {
        // 磁吸：把把手也拉到档位上。range 每次都按指针位置重算 value，
        // 所以覆写不会累积漂移，手指离开 8px 半径后自然脱吸。
        el.value = String(Math.round(shownT * SLIDER_STEPS))
      }
      if (hit.kind !== lastMagnetRef.current) {
        lastMagnetRef.current = hit.kind
        if (hit.kind !== null) buzz(hapticsRef.current, 'tick')
      }
      liveRef.current = hit.to
      paint(hit.to, shownT)
    }
    // change = pointerup / 键盘确认。此刻才允许写 React state（红线 #48）
    const onCommit = (): void => {
      onChangeRef.current(liveRef.current)
    }

    el.addEventListener('input', onInput)
    el.addEventListener('change', onCommit)
    return () => {
      el.removeEventListener('input', onInput)
      el.removeEventListener('change', onCommit)
    }
  }, [paint])

  // 外部改值（点档位 pill / 键盘输入 / 换了一手牌）时把 DOM 同步回来
  useEffect(() => {
    liveRef.current = value
    const t = mapping.toT(value)
    if (inputRef.current !== null) inputRef.current.value = String(Math.round(t * SLIDER_STEPS))
    paint(value, t)
  }, [value, mapping, paint])

  const openPad = useCallback((): void => {
    padDraftRef.current = ''
    if (padValueRef.current !== null) padValueRef.current.textContent = '—'
    if (padRef.current !== null) padRef.current.hidden = false
  }, [])

  const closePad = useCallback((): void => {
    if (padRef.current !== null) padRef.current.hidden = true
  }, [])

  /** 键盘输入同样不走 state：草稿只活在 ref + 一个文本节点里 */
  const pressKey = useCallback((key: string): void => {
    const cur = padDraftRef.current
    const next =
      key === 'del' ? cur.slice(0, -1) : key === 'clr' ? '' : (cur + key).replace(/^0+(?=\d)/, '')
    padDraftRef.current = next.slice(0, 12)
    if (padValueRef.current !== null) {
      padValueRef.current.textContent = padDraftRef.current === '' ? '—' : formatChips(Number(padDraftRef.current))
    }
  }, [])

  const commitPad = useCallback((): void => {
    const parsed = parseManualAmount(padDraftRef.current, legal, bigBlind)
    closePad()
    if (parsed !== null) onChange(parsed)
  }, [legal, bigBlind, onChange, closePad])

  const verb = legal.raiseKind === 'bet' ? '下注到' : '加注到'

  return (
    <div className="raise" role="group" aria-label="加注金额">
      <div className="raise__head">
        <span className="raise__label">{verb}</span>
        {/* 金色只用在筹码数字上（token 约定的三处之一），且坐在深色抽屉底上 */}
        <div className="raise__amount num" ref={amountRef} aria-hidden="true">
          {formatChips(value)}
        </div>
        <button type="button" className="raise__manual hit-expand" onClick={openPad}>
          手动输入
        </button>
        {onClose !== undefined && (
          <button
            type="button"
            className="raise__close hit-expand"
            aria-label="收起加注面板"
            onClick={onClose}
          >
            <IconClose size={18} />
          </button>
        )}
      </div>

      {/* 滑块在档位之上：先粗调再微调，是拇指最自然的顺序 */}
      <div className="raise__slider">
        <div className="raise__track interactive" ref={trackRef}>
          <div className="raise__fill" ref={fillRef} />
          <input
            ref={inputRef}
            className="raise__range"
            type="range"
            min={0}
            max={SLIDER_STEPS}
            step={1}
            defaultValue={Math.round(mapping.toT(value) * SLIDER_STEPS)}
            disabled={!mapping.enabled}
            aria-label={verb}
            aria-valuetext={`${verb} ${formatChips(value)}`}
          />
        </div>
        <div className="raise__range-note">
          {mapping.enabled ? (
            <>
              {/* 两端就是合法区间：滑块拖不到比最小加注更低的地方 */}
              <span className="num">最小 {formatChips(mapping.min)}</span>
              <span className="num">全下 {formatChips(mapping.max)}</span>
            </>
          ) : (
            <span>筹码不足一个完整加注，只能全下</span>
          )}
        </div>
      </div>

      <div className="raise__pills">
        {pills.map((p) => (
          <button
            key={p.kind}
            type="button"
            className="raise__pill"
            data-kind={p.kind}
            data-active={p.to !== null && p.to === value ? 'on' : 'off'}
            disabled={p.disabled}
            // 置灰而不静默上调：玩家必须看得见「这个档位不够一个最小加注」。
            // 但理由只进 title / aria —— 「不足最小加注」六个字塞进 1/5 宽的 pill 会折成三行，
            // 整排跟着变高。屏幕上那条「最小 40」已经把原因说清楚了。
            title={p.hint ?? undefined}
            aria-label={p.hint === null ? undefined : `${p.label}，${p.hint}`}
            onClick={(e) => {
              if (p.to === null) return
              buzz(haptics, 'select', e.currentTarget)
              onChange(p.to)
            }}
          >
            <span className="raise__pill-label">{p.label}</span>
            <span className="raise__pill-amt num">{p.to === null ? '—' : formatChips(p.to)}</span>
          </button>
        ))}
      </div>

      {footer !== undefined && <div className="raise__confirm">{footer}</div>}

      {/* 自绘数字键盘：默认藏起来，hidden 由 ref 直接切，避免开合也走 state */}
      <div className="raise__pad" ref={padRef} hidden role="group" aria-label="输入加注金额">
        <div className="raise__pad-view">
          <span className="raise__label">{verb}</span>
          <div className="raise__pad-value num" ref={padValueRef}>
            —
          </div>
          <span className="raise__pad-range num">
            {formatChips(legal.minRaiseTo)} – {formatChips(legal.maxRaiseTo)}
          </span>
        </div>
        <div className="raise__pad-grid">
          {['1', '2', '3', '4', '5', '6', '7', '8', '9', 'clr', '0', 'del'].map((k) => (
            <button
              key={k}
              type="button"
              className="raise__key"
              aria-label={k === 'del' ? '退格' : k === 'clr' ? '清空' : k}
              onClick={() => pressKey(k)}
            >
              {k === 'del' ? <IconBackspace /> : k === 'clr' ? '清空' : k}
            </button>
          ))}
        </div>
        <div className="raise__pad-actions">
          <button type="button" className="raise__pad-cancel" onClick={closePad}>
            取消
          </button>
          <button type="button" className="raise__pad-ok pressable" onClick={commitPad}>
            确定
          </button>
        </div>
      </div>
    </div>
  )
}

export default RaiseControl
